import { useState, type CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatearCLP, formatearPorcentajeSimple } from '@lexicore/shared';
import {
  IconoAlerta,
  IconoBasura,
  IconoCerrar,
  IconoCheck,
  IconoEditar,
  IconoIzquierda,
} from '../../componentes/Iconos';
import { Cargando, ErrorCarga, Insignia, TarjetaKpi } from '../../componentes/UI';
import { useAvisos } from '../../contextos/AvisosContext';
import { useProyecto, type SaludProyecto } from '../../lib/consultas';
import { useCambiarEstadoProyecto } from '../../lib/mutaciones';
import { ModalEditarProyecto } from './ModalEditarProyecto';
import { ModalEliminarProyecto } from './ModalEliminarProyecto';
import { PestanaBitacora } from './PestanaBitacora';
import { PestanaCierre } from './PestanaCierre';
import { PestanaFacturacion } from './PestanaFacturacion';
import { PestanaGastos } from './PestanaGastos';
import { PestanaHitos } from './PestanaHitos';
import { PestanaPresupuesto } from './PestanaPresupuesto';
import { PestanaResumen } from './PestanaResumen';

type ClavePestana = 'resumen' | 'gastos' | 'facturacion' | 'hitos' | 'presupuesto' | 'bitacora' | 'cierre';

/**
 * Ficha del proyecto: el lugar donde se trabaja.
 *
 * El CRM anterior gestionaba todo desde su modal de detalle —gastos, facturas,
 * hitos, bitácora, cierre— y esa era la parte que mejor funcionaba: cuando ya
 * estás dentro de un proyecto, el contexto lo tienes. Salir a otro módulo,
 * buscarlo en una lista y volver es trabajo que el sistema le pasa a la persona.
 *
 * Los módulos de Costos y Cobranza muestran lo mismo consolidado entre todos los
 * proyectos; aquí es donde se carga y se corrige.
 */
export function FichaProyecto() {
  const { id } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const avisos = useAvisos();
  const [pestana, setPestana] = useState<ClavePestana>('resumen');

  // Modales de Proyecto
  const [mostrarModalEditar, setMostrarModalEditar] = useState(false);
  const [mostrarModalEliminar, setMostrarModalEliminar] = useState(false);
  const [mostrarModalEstado, setMostrarModalEstado] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState<string>('');
  const [motivoEstado, setMotivoEstado] = useState<string>('');
  const cambiarEstadoMut = useCambiarEstadoProyecto();

  const { data, isLoading, isError, error, refetch } = useProyecto(id);

  if (isLoading) return <Cargando />;
  if (isError) return <ErrorCarga error={error} reintentar={() => void refetch()} />;
  if (!data) return null;

  const { metricas } = data;
  const salud: SaludProyecto =
    metricas.salud ??
    (metricas.alertas.some((alerta) => alerta.nivel === 'critico')
      ? 'CRITICO'
      : metricas.alertas.length > 0
      ? 'ALERTA'
      : 'SANO');

  const cuotasPendientes = data.cuotas.filter((cuota) => cuota.estado === 'PROYECTADA').length;
  const cierrePendiente = data.itemsCierre.filter((item) => !item.completado).length;

  const pestanas: { clave: ClavePestana; etiqueta: string; acento: string; contador?: number }[] = [
    { clave: 'resumen', etiqueta: 'Resumen', acento: 'var(--accent-blue)' },
    { clave: 'gastos', etiqueta: 'Gastos', acento: 'var(--accent-red)', contador: data.gastos.length },
    {
      clave: 'facturacion',
      etiqueta: 'Facturación',
      acento: 'var(--accent-green)',
      contador: data.facturas.length + cuotasPendientes,
    },
    { clave: 'hitos', etiqueta: 'Hitos', acento: 'var(--accent-purple)', contador: data.hitos.length },
    { clave: 'presupuesto', etiqueta: 'Presupuesto', acento: 'var(--accent-yellow)' },
    { clave: 'bitacora', etiqueta: 'Bitácora', acento: 'var(--accent-gray)', contador: data.notasBitacora.length },
    { clave: 'cierre', etiqueta: 'Cierre', acento: 'var(--accent-blue)', contador: cierrePendiente },
  ];

  const guardarCambioEstado = async () => {
    if (!nuevoEstado) return;
    try {
      await cambiarEstadoMut.mutateAsync({
        id: data.id,
        estado: nuevoEstado,
        motivo: motivoEstado.trim() || undefined,
      });
      avisos.exito(`Estado cambiado a ${nuevoEstado}`);
      setMostrarModalEstado(false);
      setMotivoEstado('');
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'No se pudo actualizar el estado.');
    }
  };

  return (
    <>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => navegar('/proyectos')} style={{ marginBottom: 18 }}>
        <IconoIzquierda size={13} />
        Volver a proyectos
      </button>

      {/* ── Cabecera: siempre visible, sin importar la pestaña ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>{data.nombre}</h1>
            <Insignia estado={data.estado} />
            <span className={`badge-salud badge-salud-${salud}`} title={metricas.motivosSalud?.join(' · ') || `Salud: ${salud}`}>
              {salud === 'SANO' && <IconoCheck size={11} />}
              {salud === 'ALERTA' && <IconoAlerta size={11} />}
              {salud === 'CRITICO' && <IconoAlerta size={11} />}
              {salud}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setMostrarModalEditar(true)}
              title="Editar datos operativos del proyecto"
            >
              <IconoEditar size={13} />
              Editar Proyecto
            </button>

            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setNuevoEstado(data.estado);
                setMotivoEstado('');
                setMostrarModalEstado(true);
              }}
            >
              Cambiar estado
            </button>

            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setMostrarModalEliminar(true)}
              style={{ color: 'var(--accent-red)' }}
              title="Eliminar proyecto definitivamente"
            >
              <IconoBasura size={13} />
              Eliminar
            </button>
          </div>
        </div>

        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 6 }}>
          {data.codigo}
          {data.organismo ? ` · ${data.organismo.nombre}` : ' · sin organismo asignado'}
          {' · inicio '}
          {data.fechaInicio.slice(0, 10)}
          {data.fechaFinPlanificada ? ` · término ${data.fechaFinPlanificada.slice(0, 10)}` : ''}
        </p>
      </div>

      <div className="kpi-grid">
        <TarjetaKpi
          etiqueta="Contrato"
          valor={formatearCLP(
            data.modalidadMonto === 'BRUTO' && data.montoContratoBruto
              ? data.montoContratoBruto
              : data.montoContratoNeto,
          )}
          acento="var(--accent-blue)"
          insignia={
            data.modalidadMonto === 'BRUTO'
              ? { texto: 'IVA incl.', color: 'var(--accent-blue)', fondo: 'rgba(33, 150, 243, 0.15)' }
              : { texto: 'Neto', color: 'var(--text-secondary)', fondo: 'var(--bg-elevated)' }
          }
          nota={
            data.modalidadMonto === 'BRUTO' && data.montoContratoBruto
              ? `Neto: ${formatearCLP(data.montoContratoNeto)} · IVA: ${formatearCLP(data.montoContratoBruto - data.montoContratoNeto)}`
              : `Total: ${formatearCLP(data.montoContratoBruto ?? Math.round(data.montoContratoNeto * 1.19))} con IVA`
          }
        />
        <TarjetaKpi
          etiqueta="Costo real"
          valor={formatearCLP(metricas.costoReal)}
          acento="var(--accent-red)"
          colorValor="var(--accent-red)"
          nota={
            data.presupuestoCostoPlanificado > 0
              ? `${formatearPorcentajeSimple(metricas.consumoPresupuestoPct)} del presupuesto`
              : 'sin presupuesto definido'
          }
        />
        <TarjetaKpi
          etiqueta="Utilidad"
          valor={formatearCLP(metricas.utilidad)}
          acento="var(--accent-green)"
          colorValor={metricas.utilidad >= 0 ? 'var(--accent-green)' : 'var(--accent-red)'}
          nota={
            metricas.margenPct === null
              ? undefined
              : `${formatearPorcentajeSimple(metricas.margenPct, 1)} de margen real (Neto)`
          }
        />
        <TarjetaKpi
          etiqueta="Días restantes"
          valor={metricas.diasRestantes === null ? '—' : metricas.diasRestantes}
          acento={metricas.diasRestantes !== null && metricas.diasRestantes < 0 ? 'var(--accent-red)' : 'var(--accent-gray)'}
          nota={
            metricas.diasRestantes !== null && metricas.diasRestantes < 0
              ? `Atrasado ${Math.abs(metricas.diasRestantes)} día(s)`
              : `${metricas.hitosCompletados} de ${metricas.hitosTotales} hitos`
          }
        />
      </div>

      {metricas.alertas.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
          {metricas.alertas.map((alerta, indice) => (
            <div
              key={indice}
              style={{
                display: 'flex',
                gap: 10,
                padding: '12px 16px',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12.5,
                lineHeight: 1.5,
                background: alerta.nivel === 'critico' ? 'var(--accent-red-soft)' : 'var(--accent-yellow-soft)',
                color: alerta.nivel === 'critico' ? 'var(--accent-red)' : 'var(--accent-yellow)',
              }}
            >
              <IconoAlerta size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{alerta.texto}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Pestañas ── */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {pestanas.map((item) => (
          <button
            key={item.clave}
            type="button"
            className={`tab-btn${pestana === item.clave ? ' activa' : ''}`}
            style={{ '--acento': item.acento } as CSSProperties}
            onClick={() => setPestana(item.clave)}
          >
            <span className="tab-punto" />
            {item.etiqueta}
            {item.contador !== undefined && item.contador > 0 && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  padding: '1px 6px',
                  borderRadius: 20,
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-muted)',
                }}
              >
                {item.contador}
              </span>
            )}
          </button>
        ))}
      </div>

      {pestana === 'resumen' && <PestanaResumen proyecto={data} />}
      {pestana === 'gastos' && <PestanaGastos proyecto={data} />}
      {pestana === 'facturacion' && <PestanaFacturacion proyecto={data} />}
      {pestana === 'hitos' && <PestanaHitos proyecto={data} />}
      {pestana === 'presupuesto' && <PestanaPresupuesto proyecto={data} />}
      {pestana === 'bitacora' && <PestanaBitacora proyecto={data} />}
      {pestana === 'cierre' && <PestanaCierre proyecto={data} />}

      {/* ── Modal para Cambio de Estado de Proyecto con Motivo de Auditoría ── */}
      {mostrarModalEstado && (
        <div className="modal-overlay" onClick={() => setMostrarModalEstado(false)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cambiar estado del proyecto</h2>
              <button
                type="button"
                className="btn-icono"
                onClick={() => setMostrarModalEstado(false)}
                aria-label="Cerrar modal"
              >
                <IconoCerrar size={14} />
              </button>
            </div>

            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <strong style={{ fontSize: 14, color: 'var(--text-primary)' }}>
                  {data.nombre}
                </strong>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {data.codigo} · Estado actual: <Insignia estado={data.estado} />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="selectFichaEstado">Nuevo estado operativo</label>
                <select
                  id="selectFichaEstado"
                  value={nuevoEstado}
                  onChange={(e) => setNuevoEstado(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="EN_EJECUCION">En ejecución</option>
                  <option value="EN_PAUSA">En pausa</option>
                  <option value="PLANIFICADO">Planificado</option>
                  <option value="CERRADO">Cerrado</option>
                  <option value="CANCELADO">Cancelado</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="textareaFichaMotivo">
                  Motivo del cambio (auditoría obligatoria para trazabilidad)
                </label>
                <textarea
                  id="textareaFichaMotivo"
                  value={motivoEstado}
                  onChange={(e) => setMotivoEstado(e.target.value)}
                  rows={3}
                  placeholder="Ej: Suspensión temporal por solicitud del cliente, o reactivación tras aprobación de hitos."
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>

              {nuevoEstado === 'EN_PAUSA' && (
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--accent-yellow-soft)',
                    color: 'var(--accent-yellow)',
                    fontSize: 12,
                    lineHeight: 1.4,
                  }}
                >
                  Pausar el proyecto suspende las alertas temporales hasta su reactivación.
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setMostrarModalEstado(false)}
                disabled={cambiarEstadoMut.isPending}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void guardarCambioEstado()}
                disabled={cambiarEstadoMut.isPending || nuevoEstado === data.estado}
              >
                {cambiarEstadoMut.isPending ? 'Guardando…' : 'Confirmar cambio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de Edición Controlada de Proyecto ── */}
      {mostrarModalEditar && (
        <ModalEditarProyecto
          proyecto={data}
          alCerrar={() => setMostrarModalEditar(false)}
          alGuardarExitoso={() => void refetch()}
        />
      )}

      {/* ── Modal de Eliminación Definitiva de Proyecto ── */}
      {mostrarModalEliminar && (
        <ModalEliminarProyecto
          proyecto={{ id: data.id, codigo: data.codigo, nombre: data.nombre }}
          alCerrar={() => setMostrarModalEliminar(false)}
          alEliminarExitoso={() => {
            navegar('/proyectos');
          }}
        />
      )}
    </>
  );
}
